import { Component, ViewChild, ChangeDetectionStrategy } from "@angular/core";
import { Router } from "@angular/router";
import { ActivatedRoute } from "@angular/router";
import { DatePipe, CurrencyPipe } from "@angular/common";
import { Subscription } from "rxjs";
import { merge, of } from "rxjs";
import { map, startWith, switchMap } from "rxjs/operators";
import { AbstractSellerComponent } from "../abstract/seller.component";
import { MatDialog } from "@angular/material/dialog";
import { MatPaginator } from "@angular/material/paginator";
import { MatSort, MatSortHeader } from "@angular/material/sort";

import { KilowantContract } from "../../entity/KilowantContract";
import { AuthService } from "../../service/auth.service";
import { AnalyticsService } from "../../service/analytics.service";
import { GeneralService } from "../../service/general.service";
import { User } from "../../entity/User";
import { ContractStatus } from "../../obj/ContractStatus";
import { MatTable, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatCellDef, MatCell, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow } from "@angular/material/table";

@Component({
    selector: "kilowant-seller-accepted-offers",
    templateUrl: "../../../html/seller/seller-accepted-offers.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [MatTable, MatSort, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatSortHeader, MatCellDef, MatCell, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow, MatPaginator, CurrencyPipe, DatePipe]
})

export class SellerHomeAcceptedOfferComponent extends AbstractSellerComponent {
    @ViewChild(MatPaginator, { static: false }) paginator: MatPaginator;
    @ViewChild(MatSort, { static: false }) sort: MatSort;

    listAcceptedOffersSubscription: Subscription | null;

    displayedColumns: string[] = ["#", "Type", "UtilityName", "LoadZone", "RateClass", "TermFrom", "TermTo", "BuyOfferPrice", "SellOfferPrice", "Fee", "SalesPrice", "TermRescission", "AccountsCount", "BatchExport", "Status", "Download"];
    total: number = 0;
    user: User = new User();
    acceptedOfferList: KilowantContract[] = null!;
    currentStart: number = 0;

    constructor(
        router: Router,
        route: ActivatedRoute,
        authService: AuthService,
        analyticsService: AnalyticsService,
        generalService: GeneralService,
        private datepipe: DatePipe,
        dialog: MatDialog
    ) { super(router, route, dialog, authService, analyticsService, generalService); }

    ngOnInit(): void {
    }

    ngAfterViewInit() {
        this._loadAcceptedOffers();
    }

    _loadAcceptedOffers(): void {
        if (this.listAcceptedOffersSubscription != null) {
            this.listAcceptedOffersSubscription.unsubscribe();
            this.listAcceptedOffersSubscription = null;
        }
        this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));
        this.listAcceptedOffersSubscription = merge(this.sort.sortChange, this.paginator.page)
            .pipe(
                startWith({}),
                switchMap(() => {
                    return this.generalService.getContractsForSeller(this.total, this.paginator.pageIndex, this.sort.active, (this.sort.direction == "desc"));
                }),
                map(data => {
                    if (data === null) {
                        return [];
                    }
                    this.currentStart = this.paginator.pageIndex * this.paginator.pageSize;
                    this.total = data.key;
                    return data.value;
                }),
        )
            .subscribe(data => { this.acceptedOfferList = data; this.refreshView(); });
    }

    markAcceptedOfferNotNew(contract: KilowantContract): void {
        if (contract.isNewRecord) {
            this.generalService.markContractViewed(contract)
                .subscribe({
                    next: (res: any) => {
                        if (res) {
                            this._loadAcceptedOffers();
                        } },
                    error: (err: any) => { }
                });
        }
    }

    verifyContract(contract: KilowantContract) {
        if (contract.isNewRecord) {
            this.generalService.markContractViewed(contract)
                .subscribe({
                    next: (res: any) => {
                        this.router.navigate(["/seller/priceplans/verify", contract.id]);
                    },
                    error: (err: any) => { }
                });
        } else {
          this.router.navigate(["/seller/priceplans/verify", contract.id]);
        }
    }

    viewContract(contract: KilowantContract) {
        if (contract.isNewRecord) {
            this.generalService.markContractViewed(contract)
                .subscribe(
                    {
                        next: (res: any) => {
                            this.router.navigate(["/seller/priceplans/view", contract.id]);
                        },
                        error: (err: any) => { }
                    });
        } else {
          this.router.navigate(["/seller/priceplans/view", contract.id]);
        }
    }

    isDeclined(status: ContractStatus): boolean {
        return (status == ContractStatus.DECLINED_BY_SELLER);
    }
    isDeclinedByBuyer(status: ContractStatus): boolean {
        return (status == ContractStatus.DECLINED_BY_BUYER);
    }
    isOpen(status: ContractStatus): boolean {
        return (status == ContractStatus.OPEN);
    }
    isPending(status: ContractStatus): boolean {
        return (status == ContractStatus.PENDING);
    }
    isConfirmed(status: ContractStatus, canBeCancelled: boolean): boolean {
        return (((status == ContractStatus.PENDING || status == ContractStatus.OPEN) && !canBeCancelled) || status == ContractStatus.CLOSED);
    }
    isRescission(status: ContractStatus, canBeCancelled: boolean): boolean {
        return (status == ContractStatus.PENDING || status == ContractStatus.OPEN) && canBeCancelled;
    }
    isPendingVerification(status: ContractStatus): boolean {
        return (status == ContractStatus.PENDING_NON_VERIFIED);
    }
    isDeclinedTimout(status: ContractStatus): boolean {
        return (status == ContractStatus.DECLINED_VERIFICATION_TIMEOUT);
    }

    exportContract(contract: KilowantContract): void {
        var a = document.createElement("a");
        document.body.appendChild(a);
        this.generalService.getContractAsPDF(contract.id)
            .subscribe((data: any) => {
                var blob = new Blob([data], { type: "octet/stream" });
                var url = window.URL.createObjectURL(blob);
                a.href = url;
                a.download = "kilowant-" + contract.id + ".pdf";
                a.click();
                window.URL.revokeObjectURL(url);
                a.remove();
            });
    }

    getBatchExportFile(batch: string): void {
        var a = document.createElement("a");
        document.body.appendChild(a);
        this.generalService.getBatchExport(batch)
            .subscribe((data: any) => {
                var blob = new Blob([data], { type: "octet/stream" });
                var url = window.URL.createObjectURL(blob);
                a.href = url;
                a.download = "kilowant.csv";
                a.click();
                window.URL.revokeObjectURL(url);
                a.remove();
            });
    }

    ngOnDestroy() {
        if (this.listAcceptedOffersSubscription != null) {
            this.listAcceptedOffersSubscription.unsubscribe();
        }
    }
}
